import {
  IMultipleRowActions,
  Modal,
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import {
  MutableRefObject,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import { IGerAcessoVisitControlPacientLog } from "../../../../models/ger-acesso-visit-control-pacient-log";
import GerAcessoVisitControlPacientDetailsModal from "./GerAcessoVisitControlPacientDetailsModal";
import { restartAccessControlPacientSend } from "../../../../services/accessControlPacientSend.service";

const ACCESS_CONTROL_PACIENT_SEND_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/AccessControlPacientSend/AccessControlPacientSends";

const gerAcessoVisitControlPacientService =
  new MainApi<IGerAcessoVisitControlPacientLog>(
    ACCESS_CONTROL_PACIENT_SEND_ROUTE,
  );

const translationPath = "page.4uBus.logs.gerAcessoVisitControlPacient.";

const isErrorStatus = (status?: string | null) => {
  if (!status) return false;

  const normalizedStatus = status.toUpperCase();

  return normalizedStatus === "E" || normalizedStatus === "ERRO";
};

const getSelectedIdentifiers = (
  api: MutableRefObject<GridApiCommunity>,
) => {
  const selectedRows = api.current?.getSelectedRows?.();

  if (!selectedRows) return [];

  return Array.from(selectedRows.values())
    .map((row) => Number(row.identifier))
    .filter((identifier) => !Number.isNaN(identifier));
};

const GerAcessoVisitControlPacient = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [massRestartLoading, setMassRestartLoading] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IGerAcessoVisitControlPacientLog | null>(null);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    identifier: "",
    pacientName: "",
    documentCompanion: "",
    statusSend: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { setMessage } = useNotificationContext();

  const {
    sendRequest: restartAccessControlPacientSendReq,
  } = useFetch(restartAccessControlPacientSend);

  const columns: GridColDef<IGerAcessoVisitControlPacientLog>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 0.7,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "patientName",
      headerName: t(translationPath + "patient_name") || "Patient Name",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value ?? "-"}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "patientEncounterNumber",
      headerName: t(translationPath + "service_number") || "Service Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "patientCompanionDate",
      headerName: t(translationPath + "visit_date") || "Visit Date",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "statusSend",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          const configs: Record<
            string,
            { color: "warning" | "info" | "success" | "error"; key: string }
          > = {
            P: { color: "warning", key: "pending" },
            PENDENTE: { color: "warning", key: "pending" },
            ENVIANDO: { color: "info", key: "sending" },
            PR: { color: "info", key: "processing" },
            PROCESSANDO: { color: "info", key: "processing" },
            F: { color: "success", key: "finished" },
            FINALIZADO: { color: "success", key: "finished" },
            E: { color: "error", key: "error" },
            ERRO: { color: "error", key: "error" },
          };

          return (
            configs[status] || { color: "warning" as const, key: "pending" }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "exitDate",
      headerName: t(translationPath + "exit_date") || "Exit Date",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "patientCompanionName",
      headerName: t(translationPath + "visitor") || "Visitor",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "documentCompanion",
      headerName: t(translationPath + "visitor_cpf") || "Visitor CPF",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleRestartAccessControlPacientSend = useCallback(
    async (identifier: number) => {
      if (Number.isNaN(identifier)) {
        setMessage({
          message: t(`${translationPath}select_error_rows`),
          type: "error",
        });
        return;
      }

      const { success } = await restartAccessControlPacientSendReq({
        identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(`${translationPath}resetedSuccess`),
          type: "success",
        });
      }
    },
    [onRefreshHandler, restartAccessControlPacientSendReq, setMessage, t],
  );

  const handleMassRestartAccessControlPacientSend = useCallback(
    async (
      identifiers: number[],
      api: MutableRefObject<GridApiCommunity>,
    ) => {
      if (massRestartLoading) return;

      const validIdentifiers = identifiers.filter(
        (identifier) => !Number.isNaN(identifier),
      );

      if (validIdentifiers.length === 0) {
        setMessage({
          message: t(`${translationPath}select_error_rows`),
          type: "error",
        });
        return;
      }

      setMassRestartLoading(true);

      try {
        const results = await Promise.allSettled(
          validIdentifiers.map((identifier) =>
            restartAccessControlPacientSend({ identifier }),
          ),
        );

        const allRequestsSucceeded = results.every(
          (result) => result.status === "fulfilled",
        );

        onRefreshHandler();
        api.current.setRowSelectionModel([]);

        setMessage({
          message: allRequestsSucceeded
            ? t(`${translationPath}resetedSuccess`)
            : t(`${translationPath}bulk_resync_error`),
          type: allRequestsSucceeded ? "success" : "error",
        });
      } finally {
        setMassRestartLoading(false);
      }
    },
    [massRestartLoading, onRefreshHandler, setMessage, t],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (_, api) => {
          await handleMassRestartAccessControlPacientSend(
            getSelectedIdentifiers(api),
            api,
          );
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "resync") || "Forçar reenvio",
      },
    ],
    [handleMassRestartAccessControlPacientSend, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <GerAcessoVisitControlPacientDetailsModal
            gerAcessoVisitControlPacientData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Table<IGerAcessoVisitControlPacientLog>
        title={t(translationPath + "title")}
        columns={columns}
        service={gerAcessoVisitControlPacientService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        syncing={massRestartLoading}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          isErrorStatus(params.row.statusSend)
            ? {
                action: () => {
                  handleRestartAccessControlPacientSend(params.row.identifier);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Forçar reenvio",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            identifier: filterParams.identifier
              ? Number(filterParams.identifier)
              : undefined,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default GerAcessoVisitControlPacient;
